import HireInvoiceItem from '@shared/hireInvoice/HireInvoiceItem';

type PendingAmendmentLike = {
	originalItem: { id: number };
	amendmentItem: HireInvoiceItem & { id: number; hireInvoiceId: number | null };
	reversalItem: HireInvoiceItem & { id: number; hireInvoiceId: number | null };
};

export type LinkedHireInvoiceItem = {
	id: number;
	hireInvoiceId: number | null;
	isAmendment?: boolean | null;
	isReversal?: boolean | null;
} | null | undefined;

const getPendingAmendments = <T extends PendingAmendmentLike>(amendments: T[]): T[] => (
	amendments.filter((a) => (
		a.amendmentItem.hireInvoiceId == null &&
		a.reversalItem.hireInvoiceId == null
	))
);

const getTerminalPendingAmendments = <T extends PendingAmendmentLike>(pendingAmendments: T[]): T[] => {
	const latestByOriginalItemId = new Map<number, T>();

	pendingAmendments.forEach((a) => {
		const existing = latestByOriginalItemId.get(a.originalItem.id);

		if (existing == null || a.amendmentItem.id > existing.amendmentItem.id) {
			latestByOriginalItemId.set(a.originalItem.id, a);
		}
	});

	const originalItemIds = new Set(latestByOriginalItemId.keys());

	return Array.from(latestByOriginalItemId.values())
		.filter((a) => !originalItemIds.has(a.amendmentItem.id));
};

export const getPendingAmendmentItems = <T extends PendingAmendmentLike>(
	amendments: T[],
	isIncluded: (amendment: T) => boolean = () => true,
) => {
	const pendingAmendments = getPendingAmendments(amendments);

	return {
		supersededOriginalIds: new Set(pendingAmendments.map((a) => a.originalItem.id)),
		items: getTerminalPendingAmendments(pendingAmendments)
			.filter(isIncluded)
			.flatMap((a) => [
				HireInvoiceItem.fromJSON(a.amendmentItem),
				HireInvoiceItem.fromJSON(a.reversalItem),
			]),
	};
};

export const isUninvoicedSourceItem = (
	linkedItem: LinkedHireInvoiceItem,
	supersededOriginalIds: Set<number>,
) => (
	linkedItem == null || (
		!linkedItem.isAmendment &&
		!linkedItem.isReversal &&
		linkedItem.hireInvoiceId == null &&
		!supersededOriginalIds.has(linkedItem.id)
	)
);

export const linkChildrenToParents = (items: HireInvoiceItem[]) => {
	items.forEach((item) => {
		item.children.forEach((child) => {
			child.parentId = item.id;
		});
	});
};
